import type { CaseLink, CaseLinkConfidence, CaseLinkRelation, CaseLinkOrigin } from './cases';
import type { LienAiDocument, LienAiMatch } from './liens';
import type { RecordSummary } from './types';

/**
 * The stored AI artifacts' shapes and signatures, without the model client.
 * Readers such as the web app import this, so their bundles never carry the
 * prompts or the email writer that `ai.ts` holds.
 */

export type RecordEnrichment = {
  kind: string;
  recordId: string;
  signature: string;
  title: string;
  description: string;
  outcome?: string;
  classification: 'informational' | 'action_required';
  reason: string;
  /** What the owner would do next, when the facts imply a step; absent otherwise. */
  nextStep?: string;
  model: string;
  /** `propertyAiGenerator` at generation time; absent on older artifacts. */
  generator?: string;
};

/**
 * What the rest of the property says about one record. Deterministic, so it
 * is part of the record signature: when the surrounding facts change the
 * explanation is regenerated.
 */
export type AiPropertyContext = {
  /** Records the case layer ties to this one and how. */
  relatedRecords: Array<{
    key: string;
    relation: CaseLinkRelation;
    origin: CaseLinkOrigin;
    kind: string;
    id: string;
    status: string;
    date: string;
    description: string;
    archived: boolean;
    latestEvent: CaseAiRecord['events'][number] | null;
  }>;
  /** Dated city activity extracted from this record's own pages, newest first. */
  events: CaseAiRecord['events'];
  /** For a recorded lien: the later arm's-length deed or new loan that would have required clearing it. */
  clearedBy?: { date: string; titles: string[] } | null;
  /** For a recorded lien: it names none of the buyers on the latest sale, so it was recorded against a previous owner. */
  priorOwner?: boolean;
  /** For SF311: whether the category is one the owner answers for (building, private graffiti, sidewalk, construction noise). */
  ownerFacingCategory?: boolean;
  /** For a Public Works street permit: whether it names this property, another address on the block, or only the block. */
  streetWorkPlacement?: 'address' | 'neighbor' | 'block';
};

export type AiRecord = Pick<RecordSummary, 'kind' | 'id' | 'description' | 'status' | 'date'> & {
  context?: unknown;
  property?: AiPropertyContext;
};

/** One record as supplied to the case-level prompts. */
export type CaseAiRecord = {
  key: string;
  kind: string;
  id: string;
  title: string;
  status: string;
  date: string;
  archived: boolean;
  /** Dated city text (updates, holds, inspections) newest first. */
  events: Array<{
    date: string;
    label: string;
    status: string;
    comment: string;
  }>;
};

export type CaseStory = {
  caseKey: string;
  signature: string;
  headline: string;
  /** Sentences each citing the record keys they rest on. */
  story: Array<{ text: string; recordKeys: string[] }>;
  model: string;
  generator?: string;
};

/** Model-proposed links that survived validation, stored once per property. */
export type CaseLinksEnrichment = {
  kind: typeof CASE_LINKS_KIND;
  recordId: typeof CASE_LINKS_RECORD_ID;
  signature: string;
  links: CaseLink[];
  model: string;
  generator?: string;
};
/** Model-proposed lien/release pairings that survived validation, stored once per property. */
export type LienMatchesEnrichment = {
  kind: typeof LIEN_MATCHES_KIND;
  recordId: typeof LIEN_MATCHES_RECORD_ID;
  signature: string;
  matches: LienAiMatch[];
  model: string;
  generator?: string;
};
export type CaseStoryEnrichment = CaseStory & {
  kind: typeof CASE_STORY_KIND;
  recordId: string;
};
/** Everything the enrichment table holds for one parcel. */
export type PropertyEnrichment =
  | RecordEnrichment
  | CaseLinksEnrichment
  | CaseStoryEnrichment
  | LienMatchesEnrichment;

export const CASE_LINKS_KIND = 'case-links';
export const CASE_LINKS_RECORD_ID = 'property';
export const CASE_STORY_KIND = 'case-story';
export const LIEN_MATCHES_KIND = 'lien-matches';
export const LIEN_MATCHES_RECORD_ID = 'property';

export function isRecordEnrichment(item: PropertyEnrichment): item is RecordEnrichment {
  return (
    item.kind !== CASE_LINKS_KIND &&
    item.kind !== CASE_STORY_KIND &&
    item.kind !== LIEN_MATCHES_KIND
  );
}

const CASE_LINK_RELATIONS: ReadonlySet<CaseLinkRelation> = new Set([
  'references',
  'complaint-nov',
  'same-work',
  'permit-family',
  'recorded-order',
  'abatement-permit',
  'inspection-violation',
]);
const CASE_LINK_CONFIDENCE: ReadonlySet<CaseLinkConfidence> = new Set(['high', 'medium', 'low']);
const DAY = 86_400_000;

const COMPATIBLE_KINDS: Array<[Set<string>, Set<string>]> = [
  [
    new Set(['building', 'electrical', 'plumbing']),
    new Set(['building', 'electrical', 'plumbing', 'nov', 'complaint', 'fire-violation', '311']),
  ],
  [new Set(['recorder']), new Set(['nov', 'complaint', 'recorder'])],
  [
    new Set(['nov', 'complaint']),
    new Set(['nov', 'complaint', 'building', 'electrical', 'plumbing', 'recorder', '311']),
  ],
  [
    new Set(['fire-violation', 'fire-inspection']),
    new Set(['fire-violation', 'fire-inspection', 'electrical', 'building']),
  ],
  [new Set(['311']), new Set(['complaint', 'nov', 'building', 'electrical', 'plumbing'])],
];

/** Record kinds the case-level prompts can relate at all. */
export const LINKABLE_KINDS: ReadonlySet<string> = new Set(
  COMPATIBLE_KINDS.flatMap(([left, right]) => [...left, ...right]),
);

function kindsCompatible(a: string, b: string) {
  return COMPATIBLE_KINDS.some(
    ([left, right]) => (left.has(a) && right.has(b)) || (left.has(b) && right.has(a)),
  );
}

function recordText(record: CaseAiRecord) {
  return [
    record.title,
    ...record.events.flatMap((event) => [event.label, event.status, event.comment]),
  ].join('\n');
}

/**
 * The model only proposes; every proposal must survive these checks before it
 * can join two records into one case. Wrong merges are worse than none.
 */
export function validateCaseLinkProposals(
  proposals: unknown,
  records: CaseAiRecord[],
  parseDate: (value: string) => number | null,
): CaseLink[] {
  if (!Array.isArray(proposals)) return [];
  const byKey = new Map(records.map((record) => [record.key, record]));
  const accepted = new Map<string, CaseLink>();
  for (const item of proposals) {
    if (!item || typeof item !== 'object') continue;
    const value = item as Record<string, unknown>;
    const source = typeof value.source === 'string' ? value.source : '';
    const target = typeof value.target === 'string' ? value.target : '';
    const relation = value.relation;
    const confidence = value.confidence;
    const basis = typeof value.basis === 'string' ? value.basis.trim() : '';
    const a = byKey.get(source);
    const b = byKey.get(target);
    if (
      !a ||
      !b ||
      source === target ||
      typeof relation !== 'string' ||
      !CASE_LINK_RELATIONS.has(relation as CaseLinkRelation) ||
      typeof confidence !== 'string' ||
      !CASE_LINK_CONFIDENCE.has(confidence as CaseLinkConfidence) ||
      basis.length < 8 ||
      basis.length > 240
    )
      continue;
    if (!recordText(a).includes(basis) && !recordText(b).includes(basis)) continue;
    if (!kindsCompatible(a.kind, b.kind)) continue;
    const [timeA, timeB] = [parseDate(a.date), parseDate(b.date)];
    if (timeA !== null && timeB !== null && Math.abs(timeA - timeB) > 5 * 365 * DAY) continue;
    const key = [source, target].sort().join('|');
    if (accepted.has(key)) continue;
    accepted.set(key, {
      source,
      target,
      relation: relation as CaseLinkRelation,
      origin: 'inferred',
      confidence: confidence as CaseLinkConfidence,
      basis,
    });
  }
  return [...accepted.values()];
}

/** Non-security fingerprint of everything a case story is generated from. */
export function caseSignature(caseKey: string, records: CaseAiRecord[], links: CaseLink[] = []) {
  return fingerprint(['case', caseKey, records, links]);
}

export function caseLinksSignature(records: CaseAiRecord[]) {
  return fingerprint(['case-links', records]);
}

export function lienMatchesSignature(documents: LienAiDocument[]) {
  return fingerprint(['lien-matches', documents]);
}

const PERMIT_KINDS = new Set(['building', 'electrical', 'plumbing']);
const GENERIC_PERMIT_DESCRIPTIONS = new Set(['open the official dbi record for full details']);

/** Match duplicated permit work despite source prefixes and punctuation noise. */
export function relatedPermitKey(kind: string, description: string) {
  if (!PERMIT_KINDS.has(kind)) return '';
  const normalized = description
    .toLowerCase()
    .replace(/^online\s+(?:building|electrical|plumbing)\s+permit\s*:?\s*/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  return !GENERIC_PERMIT_DESCRIPTIONS.has(normalized) &&
    normalized.length >= 16 &&
    normalized.split(' ').length >= 3
    ? normalized
    : '';
}

function stable(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stable);
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
        .map(([key, item]) => [key, stable(item)]),
    );
  }
  return value;
}

/** Non-security fingerprint: all model inputs participate, independent of object key order. */
export function recordSignature(record: AiRecord) {
  return fingerprint([
    record.kind,
    record.id,
    record.description ?? '',
    record.status ?? '',
    record.date ?? '',
    record.context ?? null,
    record.property ?? null,
  ]);
}

/**
 * Signature of the record-only inputs (no property context). A stored
 * explanation generated under it is still accurate for an unchanged record,
 * so readers accept it until the worker replaces it with the current shape.
 */
export function legacyRecordSignature(record: AiRecord) {
  return fingerprint(
    [
      record.kind,
      record.id,
      record.description ?? '',
      record.status ?? '',
      record.date ?? '',
      record.context ?? null,
    ],
    'v5',
  );
}

export function fingerprint(input: unknown, version = 'v6') {
  const value = JSON.stringify(stable(input));
  let first = 2166136261;
  let second = 3339675911;
  for (let index = 0; index < value.length; index++) {
    const character = value.charCodeAt(index);
    first = Math.imul(first ^ character, 16777619);
    second = Math.imul(second ^ character, 2246822519);
  }
  return `${version}:${(first >>> 0).toString(16).padStart(8, '0')}${(second >>> 0).toString(16).padStart(8, '0')}`;
}
